import {
    API_ENDPOINTS,
    BottomAnchoredControls,
    Button,
    ButtonTypes,
    Card,
    CardBody,
    customToast,
    PageLayout,
    replaceKeyWithValue,
    route,
    Stepper,
    StringHelpers,
    useDisabledContext,
    useForm,
    useLangContext,
    useNavigationGuard,
    useStepper,
    Variants,
    WidthConstrainedContainer,
} from 'carrier-fe';
import { useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CLAIM_STEPS, CLAIM_TOTAL_STEPS, DEFAULT_NAVIGATION_GUARD } from '../../../util/Constants';
import { configureClaimsPayload } from '../../../util/claims-utils';
import ClaimsStep1 from '../../../components/claims-journey/Step1';
import ClaimsStep2 from '../../../components/claims-journey/Step2';
import ClaimsStep3 from '../../../components/claims-journey/Step3';
import ClaimsStep4 from '../../../components/claims-journey/Step4';
import { ClaimsFormData } from '../../../types/claim';

const defaultData: ClaimsFormData = {
    // Step 1
    company_id: '',
    account_number: '',
    toshiba_installation_id: '',
    installation_date: '',
    customer_id: '',
    distributor_id: '',
    // Step 2
    toshiba_installation_system_id: '',
    product_child_id: '',
    product_type_id: '',
    product_line_id: '',
    product_id: '',
    serial_number: '',
    dispatch_note_number: '',
    date_of_failure: '',
    date_of_repair: '',
    proposed_date_of_repair: '',
    quantity_of_parts: '',
    // Step 3
    parts: [],
    // Step 4 - Summary (read-only, no additional fields)
};

function ClaimsJourney() {
    // Hooks and Contexts
    const navigate = useNavigate();
    const { crud } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();

    const form = useForm(
        validateAndProceed,
        defaultData,
        { silentSuccess: true }
    );

    const { Submit, setValues, store } = form;

    const { activeStep, nextStep, prevStep, isFirstStep, isLastStep } = useStepper({
        totalSteps: CLAIM_TOTAL_STEPS,
        initialStep: CLAIM_STEPS.STEP_1,
    });

    useNavigationGuard({
        message: crud?.modals?.alert?.description?.at(0) || DEFAULT_NAVIGATION_GUARD,
        isEnabled: !isLastStep,
    });

    // Derived States
    const title = useMemo(
        () => replaceKeyWithValue(
            crud?.pages?.create?.title || 'Create :model',
            'model',
            crud?.models?.claim || 'Claim'
        ),
        [crud]
    );

    const btnLabel = useMemo(() => {
        const stepType = isLastStep ? 'confirm' : 'next';
        const defaultLabel = crud?.buttons?.[stepType]?.default || (isLastStep ? 'Confirm' : 'Next');
        const loadingLabel = crud?.buttons?.[stepType]?.submitting || (isLastStep ? 'Confirming...' : 'Saving...');

        return { loadingLabel, defaultLabel };
    }, [isLastStep, crud?.buttons]);

    async function validateAndProceed(data: Partial<ClaimsFormData>): Promise<any> {
        const payload = configureClaimsPayload(data, activeStep);

        // On last step, submit the full claim
        if (isLastStep) {
            const response = await axios.post<{
                data: ClaimsFormData & { id: string };
                message: string;
            }>(
                route(API_ENDPOINTS.REGISTRATION.CLAIM.STORE),
                data // Send full data for final submission
            );

            if (response?.status === 201) {
                setDisabled(false);
                navigate(`/claims/${response.data?.data?.id}`);
                customToast({
                    title: response.data?.message,
                    variant: Variants.Success,
                });
            }
        } else {
            // Validate step without persisting
            const response = await axios.post<{
                data: Partial<ClaimsFormData>;
                message: string;
            }>(
                route(API_ENDPOINTS.REGISTRATION.CLAIM.VALIDATE_STEP, {
                    step: String(activeStep),
                }),
                payload
            );

            if (response?.status === 200) {
                nextStep();
            }
        }
    }

    const renderBackButton = () => {
        if (isFirstStep) return false;
        return activeStep >= CLAIM_STEPS.STEP_2;
    };

    const stepComponents = [
        ClaimsStep1,
        ClaimsStep2,
        ClaimsStep3,
        ClaimsStep4,
    ];

    return (
        <PageLayout
            title={StringHelpers.title(title)}
            progressBar={{
                activeStep,
                totalSteps: CLAIM_TOTAL_STEPS,
            }}
        >
            <WidthConstrainedContainer>
                <Card style={{ maxWidth: '1440px' }}>
                    <CardBody>
                        <div>
                            <Stepper
                                title={StringHelpers.upper(
                                    `${crud?.sub_titles?.step || 'step'} ${activeStep}/${CLAIM_TOTAL_STEPS}`
                                )}
                                activeStep={activeStep}
                            >
                                {stepComponents.map((StepComponent, index) => (
                                    <StepComponent
                                        key={index}
                                        form={form}
                                    />
                                ))}
                            </Stepper>
                        </div>
                    </CardBody>
                </Card>
            </WidthConstrainedContainer>
            <BottomAnchoredControls>
                <div className="me-auto ms-5">
                    <Button
                        label={StringHelpers.title(crud?.buttons?.exit?.default || 'Exit')}
                        onClick={() => {
                            window.confirm(
                                crud?.modals?.alert?.description?.at(0) ||
                                DEFAULT_NAVIGATION_GUARD
                            ) &&
                            navigate('/claims');
                        }}
                        variant={Variants.Dark}
                        type={ButtonTypes.Outline}
                        disabled={disabled}
                    />
                </div>
                {renderBackButton() && (
                    <Button
                        label={crud?.buttons?.previous?.default || 'Previous'}
                        onClick={prevStep}
                        variant={Variants.Dark}
                        type={ButtonTypes.Outline}
                        className="me-4"
                        disabled={disabled}
                    />
                )}
                <Submit
                    disabled={disabled}
                    defaultLabel={btnLabel.defaultLabel}
                    loadingLabel={btnLabel.loadingLabel}
                />
            </BottomAnchoredControls>
        </PageLayout>
    );
}

export default ClaimsJourney;
